import * as layout from '../../../components/markLayout.module.css';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useCallback, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import LinkButton from '@hero/ui-kit/inputs/LinkButton';
import P from '@hero/ui-kit/typography/P';
import useCreateBlast from '../../../api/useCreateBlast';
import useUpdateBlast from '../../../api/useUpdateBlast';
import useUnsavedChangesRegistration from '../../../hooks/useUnsavedChangesRegistration';
import {
    extractBlastErrorMessage,
    getScheduleError,
    getLocalSendTimeError,
    toUtcIso
} from '../blastFormUtils';
import Step1Structure from './Step1Structure';
import Step2Audience from './Step2Audience';
import Step3Review from './Step3Review';
import type { Step1Data } from './Step1Structure';
import type { Step2Data } from './Step2Audience';
const STEPS = ['Structure', 'Audience & schedule', 'Review'] as const;
// A plain history.replaceState, not router navigation — setSearchParams would
// route through useBlocker, which pops the "discard unsaved changes?" modal
// since this wizard registers dirty as soon as any field changes.
const stampResumeUrl = (id: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('resume', String(id));
    window.history.replaceState(window.history.state, '', url);
};
const clearResumeUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('resume');
    window.history.replaceState(window.history.state, '', url);
};
const readResumeIdFromUrl = (): number | null => {
    const raw = new URLSearchParams(window.location.search).get('resume');
    const n = raw ? Number(raw) : null;
    return n != null && Number.isInteger(n) && n > 0 ? n : null;
};
const NewBlast: React.FC = () => {
    const navigate = useNavigate();
    const [resumeIdFromUrl] = useState(readResumeIdFromUrl);
    const createBlast = useCreateBlast();
    const updateBlast = useUpdateBlast();
    const stepNavId = useId();
    const [dirty, setDirty] = useState(false);
    const [step, setStep] = useState(0);
    const [furthestStep, setFurthestStep] = useState(0);
    const [step1, setStep1] = useState<Step1Data>({
        name: '',
        messageClass: 'MARKETING',
        deliveryScope: 'PER_EMAIL',
        variants: [{ label: 'Variant A', ratio: 100 }]
    });
    const [step2, setStep2] = useState<Step2Data>({
        segmentId: null,
        segmentName: null,
        accountRecipients: 'ALL',
        fromAddress: '',
        scheduleMode: 'immediate',
        scheduledAt: '',
        localSendTime: ''
    });
    const [step1Errors, setStep1Errors] = useState<string[]>([]);
    const [step2Errors, setStep2Errors] = useState<string[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [createdBlastId, setCreatedBlastId] = useState<number | null>(null);
    const isSaving = createBlast.isPending || updateBlast.isPending;
    const unregister = useUnsavedChangesRegistration('blast-create', dirty, isSaving);
    const stepRefs = useRef<(HTMLDivElement | null)[]>([null, null, null]);
    const stepHeadingRefs = useRef<(HTMLHeadingElement | null)[]>([null, null, null]);
    const submittingRef = useRef(false);
    const focusStep = (index: number) => {
        requestAnimationFrame(() => stepHeadingRefs.current[index]?.focus());
    };
    const focusField = (index: number, field?: string | null) => {
        requestAnimationFrame(() => {
            const container = stepRefs.current[index];
            if (!container) return;
            if (field) {
                const target = container.querySelector<HTMLElement>(`[data-field="${field}"]`);
                if (target) {
                    target.focus();
                    return;
                }
            }
            const fallback = container.querySelector<HTMLElement>(
                'input:not(:disabled), select:not(:disabled), textarea:not(:disabled)'
            );
            if (fallback) {
                fallback.focus();
            } else {
                stepHeadingRefs.current[index]?.focus();
            }
        });
    };
    const handleStep1Change = useCallback((data: Step1Data) => {
        setDirty(true);
        setStep1(data);
        setStep1Errors([]);
    }, []);
    const handleStep2Change = useCallback((data: Step2Data) => {
        setDirty(true);
        setStep2(data);
        setStep2Errors([]);
    }, []);
    const validateStep1 = (): {
        errors: string[];
        focus: string | null;
    } => {
        const errs: string[] = [];
        let focus: string | null = null;
        if (!step1.name.trim()) {
            errs.push('Name is required.');
            focus ??= 'name';
        }
        if (step1.variants.length === 0) {
            errs.push('At least one variant is required.');
            focus ??= 'variant-add';
        }
        const ratioSum = step1.variants.reduce((s, v) => s + v.ratio, 0);
        const ratioInvalid = ratioSum !== 100;
        if (ratioInvalid) {
            errs.push('Variant ratios must sum to 100.');
        }
        const labels = step1.variants.map((v) => v.label.trim().toLowerCase());
        const duplicateLabelIndex = labels.findIndex(
            (label) => labels.indexOf(label) !== labels.lastIndexOf(label)
        );
        if (duplicateLabelIndex >= 0) {
            errs.push('Variant labels must be unique.');
        }
        const emptyLabelIndex = step1.variants.findIndex((v) => !v.label.trim());
        if (emptyLabelIndex >= 0) {
            errs.push('Every variant needs a label.');
        }
        if (focus === null && step1.variants.length > 0) {
            const invalidFields = [
                ...(ratioInvalid ? [{ position: 1, field: 'variant-ratio-0' }] : []),
                ...(duplicateLabelIndex >= 0
                    ? [
                          {
                              position: duplicateLabelIndex * 2,
                              field: `variant-label-${duplicateLabelIndex}`
                          }
                      ]
                    : []),
                ...(emptyLabelIndex >= 0
                    ? [
                          {
                              position: emptyLabelIndex * 2,
                              field: `variant-label-${emptyLabelIndex}`
                          }
                      ]
                    : [])
            ];
            invalidFields.sort((a, b) => a.position - b.position);
            focus = invalidFields[0]?.field ?? null;
        }
        setStep1Errors(errs);
        return { errors: errs, focus };
    };
    const validateStep2 = (): {
        errors: string[];
        focus: string | null;
    } => {
        const errs: string[] = [];
        let focus: string | null = null;
        if (createdBlastId == null && step2.scheduleMode === 'scheduled') {
            const scheduleError = getScheduleError(step2.scheduledAt);
            const localTimeError = getLocalSendTimeError(step2.localSendTime);
            if (scheduleError) {
                errs.push(scheduleError);
                focus ??= 'scheduled-at';
            }
            if (localTimeError) {
                errs.push(localTimeError);
                focus ??= 'local-send-time';
            }
        }
        setStep2Errors(errs);
        return { errors: errs, focus };
    };
    const goNext = () => {
        if (isSaving) return;
        if (step === 0) {
            const v = validateStep1();
            if (v.errors.length > 0) {
                focusField(0, v.focus);
                return;
            }
        }
        if (step === 1) {
            const v = validateStep2();
            if (v.errors.length > 0) {
                focusField(1, v.focus);
                return;
            }
        }
        const next = Math.min(step + 1, STEPS.length - 1);
        setStep(next);
        setFurthestStep((current) => Math.max(current, next));
        focusStep(next);
    };
    const goPrev = () => {
        if (isSaving) return;
        const prev = Math.max(step - 1, 0);
        setStep(prev);
        focusStep(prev);
    };
    const goToStep = (target: number, focusHeading = true) => {
        if (isSaving || target > furthestStep) return;
        if (target > 1) {
            const validation = validateStep2();
            if (validation.errors.length > 0) {
                setStep(1);
                focusField(1, validation.focus);
                return;
            }
        }
        setStep(target);
        if (focusHeading) focusStep(target);
    };
    const handleStepKeyDown = (event: React.KeyboardEvent, index: number) => {
        let target: number | null = null;
        if (event.key === 'ArrowRight') target = Math.min(index + 1, furthestStep);
        if (event.key === 'ArrowLeft') target = Math.max(index - 1, 0);
        if (target === null || target === index) return;
        event.preventDefault();
        goToStep(target, false);
        const el = document.getElementById(`${stepNavId}-step-${target}`);
        el?.focus();
    };
    const handleSubmit = async () => {
        if (submittingRef.current) return;
        const v1 = validateStep1();
        const v2 = validateStep2();
        if (v1.errors.length > 0 || v2.errors.length > 0) {
            if (v1.errors.length > 0) {
                setStep(0);
                focusField(0, v1.focus);
            } else {
                setStep(1);
                focusField(1, v2.focus);
            }
            return;
        }
        submittingRef.current = true;
        setSubmitError(null);
        let shellCreatedThisAttempt = false;
        try {
            let blastId = createdBlastId;
            if (blastId == null) {
                const response = await createBlast.mutateAsync({
                    name: step1.name.trim(),
                    delivery_scope: step1.deliveryScope,
                    message_class: step1.messageClass,
                    ...(step2.segmentId != null && { segment_id: step2.segmentId }),
                    ...(step1.deliveryScope === 'PER_ACCOUNT' && {
                        account_recipients: step2.accountRecipients
                    }),
                    ...(step2.fromAddress.trim() && { from_address: step2.fromAddress.trim() }),
                    schedule_mode: step2.scheduleMode,
                    ...(step2.scheduleMode === 'scheduled' && {
                        scheduled_at: toUtcIso(step2.scheduledAt),
                        ...(step2.localSendTime && { local_send_time: step2.localSendTime })
                    })
                });
                blastId = response.data.blast_id;
                shellCreatedThisAttempt = true;
                setCreatedBlastId(blastId);
                // Recoverable if the follow-up below fails or the page reloads —
                // see the recovery banner below.
                stampResumeUrl(blastId);
            }
            await updateBlast.mutateAsync({
                blast_id: blastId,
                variants: step1.variants.map((variant) => ({
                    label: variant.label.trim(),
                    ratio: variant.ratio
                }))
            });
            clearResumeUrl();
            flushSync(unregister);
            navigate(`/mark/blasts/${blastId}?tab=content`);
        } catch (error) {
            const prefix =
                createdBlastId != null || shellCreatedThisAttempt
                    ? 'The blast shell was created. '
                    : '';
            setSubmitError(`${prefix}${extractBlastErrorMessage(error as AxiosError)}`);
        } finally {
            submittingRef.current = false;
        }
    };
    // Only a reload/restored tab needs this static banner — that's the case
    // with no in-memory wizard state left to retry from. A shell created
    // earlier in THIS mount keeps the wizard on Step3Review, which has its
    // own "Retry saving variants" button (shellCreated + submitError below).
    if (resumeIdFromUrl != null && !isSaving) {
        return (
            <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
                <H role="h5">Blast draft created</H>
                <P
                    noDefaultMargin
                    styles={{
                        marginTop: '0.8rem'
                    }}
                >
                    A blast was created, but this wizard didn't finish setting it up. Continue from
                    its edit page.
                </P>
                <LinkButton variant="primary" to={`/mark/blasts/${resumeIdFromUrl}/edit`}>
                    Draft created — continue editing
                </LinkButton>
            </Section>
        );
    }
    return (
        <Section subtheme={['regular', 'background']} className={layout.form} noDefaultPadding>
            <H role="h4">New blast</H>
            <P noDefaultMargin styles={{ color: 'var(--mark-muted)' }}>
                Choose the campaign structure and audience. Creation saves a draft; publishing and
                scheduling are separate actions.
            </P>
            <nav aria-label="Wizard steps">
                <ol
                    role="list"
                    style={{
                        listStyle: 'none',
                        margin: '0 0 1.6rem 0'
                    }}
                    className={layout.steps}
                >
                    {STEPS.map((label, i) => {
                        const isActive = i === step;
                        const isReachable = i <= furthestStep;
                        return (
                            <li key={label}>
                                <MarkButton
                                    type="button"
                                    id={`${stepNavId}-step-${i}`}
                                    aria-current={isActive ? 'step' : undefined}
                                    data-reachable={isReachable}
                                    disabled={
                                        !isReachable ||
                                        isSaving ||
                                        (createdBlastId != null && i !== STEPS.length - 1)
                                    }
                                    onClick={() => goToStep(i)}
                                    onKeyDown={(e) => handleStepKeyDown(e, i)}
                                    className={layout.stepButton}
                                    size="regular"
                                    style={{
                                        width: '100%',
                                        textAlign: 'left',
                                        whiteSpace: 'normal'
                                    }}
                                >
                                    {i + 1}. {label}
                                </MarkButton>
                            </li>
                        );
                    })}
                </ol>
            </nav>
            <fieldset
                disabled={isSaving}
                style={{
                    margin: 0
                }}
                className={layout.fieldset}
            >
                {step === 0 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[0] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[0] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Structure
                        </MarkHeading>
                        <Step1Structure
                            data={step1}
                            onChange={handleStep1Change}
                            errors={step1Errors}
                        />
                    </div>
                )}
                {step === 1 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[1] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[1] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Audience &amp; schedule
                        </MarkHeading>
                        <Step2Audience
                            data={step2}
                            onChange={handleStep2Change}
                            deliveryScope={step1.deliveryScope}
                            errors={step2Errors}
                        />
                    </div>
                )}
                {step === 2 && (
                    <div
                        ref={(el) => {
                            stepRefs.current[2] = el;
                        }}
                    >
                        <MarkHeading
                            role="h4"
                            ref={(el) => {
                                stepHeadingRefs.current[2] = el;
                            }}
                            tabIndex={-1}
                            style={{
                                margin: '0 0 1rem'
                            }}
                        >
                            Review
                        </MarkHeading>
                        <Step3Review
                            step1={step1}
                            step2={step2}
                            isSubmitting={isSaving}
                            shellCreated={createdBlastId != null}
                            submitError={submitError}
                            onSubmit={handleSubmit}
                        />
                    </div>
                )}
            </fieldset>
            <div className={layout.formFooter}>
                <MarkButton
                    type="button"
                    onClick={goPrev}
                    disabled={step === 0 || isSaving || createdBlastId != null}
                >
                    Back
                </MarkButton>
                {step < STEPS.length - 1 && (
                    <MarkButton
                        type="button"
                        onClick={goNext}
                        disabled={isSaving}
                        variant="primary"
                    >
                        Next
                    </MarkButton>
                )}
            </div>
        </Section>
    );
};
export default NewBlast;
